import { clsx } from 'clsx';
import type { ReactNode, ThHTMLAttributes } from 'react';
import { ariaSortFor, type SortState } from '@/app-kit/table-sort';

/**
 * A clickable column heading.
 *
 * ⚠️ A real `<button>`, not a `<th onClick>`: a heading that only responds to a
 * mouse is unreachable by keyboard. The arrow is `aria-hidden` — the `<th>`'s
 * `aria-sort` is what a screen reader reads, and announcing both would say it
 * twice. So this must sit inside a `<th>` that carries `aria-sort`: `SortTh`
 * below, or a `DataTable` column with a `sortKey`.
 *
 * The inactive arrow is rendered faintly rather than omitted, so every sortable
 * heading shows that it sorts and does not change width when the sort moves to
 * it. While `busy` (a server sort in flight), the sorted column's arrow is a
 * spinner in the same place.
 */
export function SortHeader<K extends string>({
  col, sort, onToggle, children, hint, busy,
}: {
  col: K;
  sort: SortState<K>;
  onToggle: (col: K) => void;
  children: ReactNode;
  /** What the column means. Leads the tooltip, ahead of the sort instruction. */
  hint?: string;
  /** The rows are being re-fetched for this sort; the active arrow spins. */
  busy?: boolean;
}) {
  const active = sort.key === col;
  const how = active
    ? `Sorted ${sort.dir === 'asc' ? 'ascending' : 'descending'} — click to reverse`
    : 'Sort by this column';
  return (
    <button
      type="button"
      onClick={() => onToggle(col)}
      title={hint ? `${hint}\n\n${how}` : how}
      // ⚠️ `[text-transform:inherit]`, not `uppercase`: Tailwind's preflight
      // resets text-transform on buttons, and the headings this sits in are not
      // all uppercase — forcing it would restyle a sentence-case table's labels.
      className="group relative inline-flex items-center [text-transform:inherit] hover:text-gray-900"
    >
      {children}
      {/* ⚠️ ABSOLUTE, just past the label, in the cell's padding: an arrow in
          the flow widened every sortable heading by ~14px, which pushed a wide
          table (the customer Coding tab's Edit column) past DataTable's clipped
          edge, where it could not be clicked. Out of the flow, a sortable table
          keeps exactly the column widths it had before. It is still a child
          of the button, so a click on the arrow sorts too. */}
      {active && busy ? (
        <span
          aria-hidden
          data-sort-busy
          className="absolute left-full top-1/2 ml-0.5 inline-block h-2.5 w-2.5 -translate-y-1/2 animate-spin rounded-full border-2 border-gray-300 border-t-brand-500"
        />
      ) : (
        <span
          aria-hidden
          className={clsx(
            'absolute left-full top-1/2 ml-0.5 -translate-y-1/2 text-[10px] leading-none',
            active ? 'text-brand-600' : 'text-gray-300 group-hover:text-gray-400',
          )}
        >
          {active && sort.dir === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </button>
  );
}

/**
 * A `<th>` holding a `SortHeader`, for the hand-written tables that do not use
 * `DataTable`. Every other attribute (class, title, colSpan) passes through to
 * the `<th>`, so swapping a heading in leaves the table's styling alone.
 */
export function SortTh<K extends string>({
  col, sort, onToggle, children, title, ...th
}: {
  col: K;
  sort: SortState<K>;
  onToggle: (col: K) => void;
  children: ReactNode;
} & Omit<ThHTMLAttributes<HTMLTableCellElement>, 'children' | 'aria-sort' | 'onToggle'>) {
  return (
    <th {...th} title={title} aria-sort={ariaSortFor(col, sort)}>
      {/* The button covers the heading, so it carries the th's title too —
          otherwise its own tooltip would hide the column's description. */}
      <SortHeader col={col} sort={sort} onToggle={onToggle} hint={title}>{children}</SortHeader>
    </th>
  );
}
